import {useFlag, useFlagsStatus} from '@unleash/proxy-client-react';
import useExperiment from './useExperiment';
import getOverrideParams from '../utils/parseUrlParams';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_OVERVIEW_TO_SHOP_STAGES,
} from '../constants/experiments';

/**
 * PDA-2092: fe-member-registry owns bucketing for the overviewToShopStages
 * experiment — this app only mirrors an assignment that already exists.
 * `shouldExclude` puts the flipper SDK in read-only mode (it fetches an
 * existing assignment but never creates one), so members who land here
 * without one resolve to control. The Unleash flag doubles as the kill
 * switch: off means control even for members holding a variant assignment.
 */
const useOverviewToShopStagesAssignment = () => {
  const config =
    EXPERIMENT_CONFIGS[EXPERIMENT_OVERVIEW_TO_SHOP_STAGES]?.[process.env.ENV];
  const {experimentId, featureFlag, variants} = config ?? {};

  const override = getOverrideParams(experimentId);
  const hasOverride = !!override;

  const {flagsReady} = useFlagsStatus();
  const isFlagEnabled = useFlag(featureFlag);

  const shouldReadAssignment =
    !hasOverride && flagsReady && isFlagEnabled && !!experimentId;

  const {isLoading, variant: hookVariant} = useExperiment({
    name: EXPERIMENT_OVERVIEW_TO_SHOP_STAGES,
    isEnabled: shouldReadAssignment,
    shouldExclude: true,
  });

  const resolvedVariant = hasOverride ? override : hookVariant;

  let isExperimentLoading = false;
  if (!hasOverride) {
    if (!flagsReady) {
      isExperimentLoading = true;
    } else if (isFlagEnabled) {
      isExperimentLoading = isLoading || false;
    }
  }

  const variantId = variants?.variant?.id;
  const isInVariant =
    !!variantId &&
    resolvedVariant === variantId &&
    (hasOverride || isFlagEnabled);

  return {
    experimentId,
    isInVariant,
    isExperimentLoading,
  };
};

export default useOverviewToShopStagesAssignment;
